import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnDestroy, OnInit, output, signal } from "@angular/core";
import { FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../../../utils/no-whitespace.validator';
import { MatExpansionModule } from "@angular/material/expansion";
import { CityList, CountyList, StateList } from "../../../../models/RequestQuote";
import { PropertyService } from "../../../../services/property.service";
import { forkJoin, map, Subscription } from "rxjs";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfirmationDialogComponent } from "../../../utils/confirmation-dialog/confirmation-dialog.component";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { BranchLlyodResponse, CountriesList } from "../../../../models/SystemAdmin.model";
import { ToastService } from "../../../../services/toast.service";
import { CountryType, ToastType } from "../../../../enums/config.enum";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { SpinnerComponent } from "../../../utils/spinner/spinner.component";

@Component({
    selector: 'app-branch-llyod-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatDialogModule, ReactiveFormsModule, MatSelectModule,SpinnerComponent],
    templateUrl: './branch-llyod-details.component.html',
    styleUrl: './branch-llyod-details.component.scss'
})
export class BranchLlyodDetails implements OnInit, OnDestroy{
    @Input() branchLlyodForm!: FormGroup;
    @Input() isCreateBranch!: boolean;
    @Input() countryId: string | null = null;
    branchllyodDetailsSubmitEmitter = output<boolean>();
    statesList = signal<StateList[]>([]);
    countyList = signal<CountyList[]>([]);
    citiesList = signal<CityList[]>([]);
    countryList = signal<CountriesList[]>([]);
    searchState = signal<any>('');
    searchCounty = signal<any>('');
    searchCity = signal<any>('');
    searchCountry = signal<any>('');
    filteredStatesList = signal<StateList[]>([]);
    filteredCountyList = signal<CountyList[]>([]);
    filteredCityList = signal<CityList[]>([]);
    filteredCountryList = signal<CountriesList[]>([]);
    router = inject(Router);
    activateRoute = inject(ActivatedRoute);
    dialog = inject(MatDialog);
    propertyService = inject(PropertyService);
    systemAdminService = inject(SystemAdminService);
    isLoading = signal<boolean>(false);
    toastService = inject(ToastService);
    stateSubscription: Subscription = new Subscription();
    countySubscription: Subscription = new Subscription();
    citySubscription: Subscription = new Subscription();   

 
    ngOnInit(): void {
        this.getCountryList();
        this.activateRoute.params.subscribe({
            next: (params) => {
              if(params && params['branchId']){
                this.fetchLlyodsAndStateListDetails(params['branchId']);
              }
            }
        });
  

        this.stateSubscription = this.branchLlyodForm.get('stateId')?.valueChanges.subscribe(value => {
            if (value === "null") {
                this.branchLlyodForm.get('stateId')?.setErrors({ required: true });
            }else{
                // Clear error if a valid option is selected
                this.branchLlyodForm.get('stateId')?.setErrors(null);
            }
        }) || new Subscription();

        this.countySubscription = this.branchLlyodForm.get('countyId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchLlyodForm.get('countyId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.branchLlyodForm.get('countyId')?.setErrors(null);
            }
        }) || new Subscription();

        this.citySubscription = this.branchLlyodForm.get('cityId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchLlyodForm.get('cityId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.branchLlyodForm.get('cityId')?.setErrors(null);
            }
        }) || new Subscription();
    }

    onBrokerAssignedChange(value: number) {
        this.toggleFields(value === 1);
    }


    // Enable or disable fields based on the lloydsBrokerAssigned value
    toggleFields(enable: boolean): void {
        const fields = [
            'lloydsPIN', 'lloydsBrokerLegalName', 'unitNum', 'streetNum', 'streetName', 'cityId', 'countyId', 'stateId', 'zipCode',
            'lloydsBrokerContactName', 'lloydsBrokerContactEmail', 'countryId'
        ];

        if (enable) {
            // Enable and make fields required
            fields.forEach(field => {
                const control = this.branchLlyodForm.get(field);
                control?.enable();
                // Add the 'required' validator without removing existing validators
                const existingValidators = control?.validator ? [control?.validator] : [];
                field !== 'unitNum' ? control?.setValidators([Validators.required, noWhitespaceValidator, ...existingValidators]) :
                control?.setValidators([...existingValidators]);

                control?.updateValueAndValidity(); // Revalidate the form control
            });
        } else {
            // Disable fields and remove required validator
            fields.forEach(field => {
                const control = this.branchLlyodForm.get(field);
                control?.reset();  // Reset the value to null or default value
                control?.disable();
                // Get current validators
                const currentValidators = control?.validator ? [control?.validator] : [];
                // Remove the `required` validator
                const filteredValidators = currentValidators.filter(validator => validator !== Validators.required && validator !== noWhitespaceValidator);
                // Set the remaining validators (other than required)
                control?.setValidators(filteredValidators);
                // Update validity
                control?.updateValueAndValidity();
            });
        }
    }

    allowOnlyAlphanumeric(event: KeyboardEvent): void {
        const key = event.key;
        
        // Check if the key is not an alphanumeric character (0-9, a-z, A-Z)
        if (!/^[a-zA-Z0-9 ]$/.test(key) && key !== "Backspace" && key !== "Delete" && key !== "ArrowLeft" && key !== "ArrowRight") {
            event.preventDefault(); // Prevent non-alphanumeric input
        }
    }

    fetchLlyodsAndStateListDetails(branchId: string) {
        forkJoin([
            this.systemAdminService.getBranchLlyodDetails(branchId),
            this.propertyService.getStates()
        ]).pipe(
            map(([llyodDetails, states]) => {
                return {llyodDetails, states};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    this.statesList.set(response.states);
                    this.filteredStatesList.set(response.states);
                    if(response?.llyodDetails?.lloydsBrokerAssigned === 1){
                       this.toggleFields(true);
                    }
                    // Store the bound countryId before binding
                    const boundCountryId = response?.llyodDetails?.lloydsBrokerAddress?.countryId;
                    this.onBindFormData(response.llyodDetails);
                    if(response?.llyodDetails?.lloydsBrokerAddress?.stateId){
                        // Pass the bound countryId to preserve it
                        this.getCountyAndCitiesList(Number(response?.llyodDetails?.lloydsBrokerAddress?.stateId), boundCountryId);
                    }
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
            }
        })
    }

    fetchBranchLlyodDetails(branchId: string) {
        this.systemAdminService.getBranchLlyodDetails(branchId).subscribe({
          next: (response: BranchLlyodResponse) => {
            if(response){
              this.getStatesList();
              this.onBindFormData(response);
            }
          },
          error: (error: any) => {
            console.log("error", error);
            this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
        })
    }

    onBindFormData(formValue: any) {
        this.branchLlyodForm.patchValue({
            lloydsBrokerAssigned: formValue?.lloydsBrokerAssigned ? formValue?.lloydsBrokerAssigned : 0,
            lloydsPIN: formValue?.lloydsPIN,
            lloydsBrokerLegalName: formValue?.lloydsBrokerLegalName,
            unitNum: formValue?.lloydsBrokerAddress?.unitNum,
            streetNum: formValue?.lloydsBrokerAddress?.streetNum,
            streetName: formValue?.lloydsBrokerAddress?.streetName,
            cityId: formValue?.lloydsBrokerAddress?.cityId,
            countyId: formValue?.lloydsBrokerAddress?.countyId,
            stateId: formValue?.lloydsBrokerAddress?.stateId,
            zipCode: formValue?.lloydsBrokerAddress?.zipCode,
            lloydsBrokerContactName: formValue?.lloydsBrokerContactName,
            lloydsBrokerContactEmail: formValue?.lloydsBrokerContactEmail,
            countryId: formValue?.lloydsBrokerAddress?.countryId,
            lloydsBrokerAddressId: formValue?.lloydsBrokerAddressId ? formValue?.lloydsBrokerAddressId : 0 
        });
        if(formValue?.lloydsBrokerAddressId){
            this.branchLlyodForm.get('lloydsBrokerAssigned')?.disable();
        }
    }

    onStateChange(event: MatSelectChange) {
        const selectedState = event;

        if(selectedState.value && selectedState.value !== "null"){
            this.branchLlyodForm.patchValue({countyId: null, cityId: null});
            this.getCountyAndCitiesList(Number(selectedState.value));
        }
    }

    getStateName(stateId: string | null): string {
        if (!stateId) return '';
        const state =Array.from(this.statesList().values()).flat().find(s => String(s.stateId) === String(stateId));
        return state ? state.stateName : '';
    }

    getCountyName(countyId: string | null): string {
        if (!countyId) return '';
        const county = this.countyList().find(c => String(c.countyId) === String(countyId));
        return county ? county.countyName : '';
    }

    getCityName(cityId: string | null): string {
        if (!cityId) return '';
        const city = this.citiesList().find(c => String(c.cityId) === String(cityId));
        return city ? city.cityName : '';
    }

    getCountryName(countryId: string | number | null): string {
        if (!countryId) return '';
        const country = this.countryList().find(c => String(c.countryId) === String(countryId));
        return country ? country.countryName : '';
    }

    onStateDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchState.set('');
            this.filteredStatesList.set([...this.statesList()]);
        }
    }

    onCountyDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCounty.set('');
            this.filteredCountyList.set([...this.countyList()]);
        }
    }

    onCityDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCity.set('');
            this.filteredCityList.set([...this.citiesList()]);
        }
    }

    onCountryDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCountry.set('');
            this.filteredCountryList.set([...this.countryList()]);
        }
    }

    onStateSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailState", 1200, () => {
          this.updateStateSearchTerm(value);
        });
    }

    onCountySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCounty", 1200, () => {
          this.updateCountySearchTerm(value);
        });
    }

    onCitySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCity", 1200, () => {
          this.updateCitySearchTerm(value);
        });
    }

    updateStateSearchTerm(value: string) {
        this.searchState.set(value);
        const filtered = this.statesList().filter((state) =>
            state.stateName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredStatesList.set(filtered);
    }

    updateCountySearchTerm(value: string) {
        this.searchCounty.set(value);
        const filtered = this.countyList().filter((county) =>
          county.countyName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCountyList.set(filtered);
    }

    updateCitySearchTerm(value: string) {
        this.searchCity.set(value);
        const filtered = this.citiesList().filter((city) =>
          city.cityName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCityList.set(filtered);
    }

    onCountrySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("BranchLlyodCountry", 1200, () => {
          this.updateCountrySearchTerm(value);
        });
    }

    updateCountrySearchTerm(value: string) {
        this.searchCountry.set(value);
        const filtered = this.countryList().filter((country) =>
          country.countryName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCountryList.set(filtered);
    }

    getCountryList() {
        this.systemAdminService.getCountryList().subscribe({
            next: (response: any) => {
                if(response){
                    this.countryList.set(response);
                    this.filteredCountryList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
            }
        })
    }

    onCountryChange(event: MatSelectChange) {
        const selectedCountry = event;
        if(selectedCountry.value && selectedCountry.value !== "null"){
            // Reset dependent fields when country changes
            this.branchLlyodForm.patchValue({stateId: null, countyId: null, cityId: null, zipCode: null});
            
            // Clear city, county dropdown lists
            this.countyList.set([]);
            this.citiesList.set([]);
            this.filteredCountyList.set([]);
            this.filteredCityList.set([]);
            
            // Fetch and populate states for the selected country
            const countryId = Number(selectedCountry.value);
            this.getStatesByCountryId(countryId);
        }
    }

    getStatesByCountryId(countryId: number) {
        this.isLoading.set(true);
        this.systemAdminService.getStateByCountryId(countryId).subscribe({
            next: (response: any) => {
                if(response && response?.length > 0){
                    this.statesList.set(response);
                    this.filteredStatesList.set(response);
                    this.isLoading.set(false);
                } else {
                    this.statesList.set([]);
                    this.filteredStatesList.set([]);
                    this.isLoading.set(false);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.statesList.set([]);
                this.filteredStatesList.set([]);
                this.isLoading.set(false);
                this.toastService.addNotification({message: 'System error. Failed to retrieve states. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
            }
        })
    }

    getStatesList() {
        this.propertyService.getStates().subscribe({
            next: (response:any) => {
                if(response && response?.length > 0){
                    this.statesList.set(response);
                    this.filteredStatesList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
            } 
        })
    }

    getCountyAndCitiesList(stateId: number, preserveCountryId?: number | null) {    
        forkJoin([
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId)
        ]).pipe(
            map(([counties, cities]) => {
                return {counties, cities};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    const countiesSortedList = this.propertyService.sortCountyRecords(response.counties);
                    const citiesSortedList = this.propertyService.sortCityRecords(response.cities);
                    this.countyList.set(countiesSortedList);
                    this.citiesList.set(citiesSortedList);
                    this.filteredCountyList.set(countiesSortedList);
                    this.filteredCityList.set(citiesSortedList);
                    const getCountryFromSelectedState = this.statesList().filter((state) => state.stateId === stateId);
                    // Only set countryId from state if preserveCountryId is not provided (i.e., not in edit mode with bound data)
                    // If preserveCountryId is provided, use it; otherwise, only set from state if form doesn't have a value
                    const currentCountryId = this.branchLlyodForm.get('countryId')?.value;
                    if (preserveCountryId !== undefined && preserveCountryId !== null) {
                        // Preserve the bound countryId
                        this.branchLlyodForm.patchValue({countryId: preserveCountryId});
                    } else if (getCountryFromSelectedState.length > 0 && (!currentCountryId || currentCountryId === null || currentCountryId === '')) {
                        // Only set from state if no value exists in form
                        this.branchLlyodForm.patchValue({countryId: getCountryFromSelectedState[0].countryId});
                    }
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    onCancel(): void{
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
          data: { message: "Unsaved changes will be lost if you navigate from this page. Do you want to continue?" }
        });
    
        dialog.afterClosed().subscribe((result: boolean) => {
          if(result){
            this.branchLlyodForm.reset();
            this.router.navigate(['/system-admin-dashboard/branch']);
          }
        })
    }

    onSubmit() {
        this.branchllyodDetailsSubmitEmitter.emit(true);
    }

     allowStreetNumberCharacters(event: KeyboardEvent): void {
      const allowedPattern = /^[0-9a-zA-Z\-\/ .#]$/;  
      const key = event.key;

      // allow control keys like Backspace, Delete, Tab, Arrow keys
      if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight'
      ) {
        return;
      }

      if (!allowedPattern.test(key)) {
        event.preventDefault();
      }
    }

      onZipCodeBlur() {
        
        const zipcode = this.branchLlyodForm.get('zipCode')?.value;
        const countryId = this.branchLlyodForm.get('countryId')?.value;
        
        // Only fetch state by zip code if country is US (1)
        // Exit early if zipcode is missing
        if (!zipcode) {
            return;
        }
        
        // Exit early if countryId is missing, null, undefined, or empty string
        if (countryId === null || countryId === undefined || countryId === '') {
            return;
        }
        
        // Convert to number and validate
        const countryIdNum = Number(countryId);
        if (isNaN(countryIdNum)) {
            return; // Invalid countryId, exit
        }
        
        // Only proceed if countryId equals US (1) - explicitly check for 1
        if (countryIdNum === CountryType.US) {
            this.fetchStateByZipCode(zipcode);
        }
        // If countryId is UK (2) or any other country, do nothing
    }

     fetchStateByZipCode( zipcode: string) {
        const countryId = this.branchLlyodForm.get('countryId')?.value;
        this.propertyService.getZipCode(zipcode, countryId).subscribe({
          next: (response: any) => {
            if(response && response?.State?.length > 0){
              //this.updateStateList(index, response?.State); // update State List
              this.fetchCountyAndCity(response?.State[0], response?.State, response?.County, response?.City);

              this.branchLlyodForm.patchValue({cityId: Number(response?.City[0]), countyId: Number(response?.County[0]), stateId: response?.State[0]});     

           
              
            }else{
              this.toastService.addNotification({message: "Invalid Zip Code, please enter a valid Zip Code", type: ToastType.Error}); // error notification
              this.branchLlyodForm.patchValue({cityId: '', countyId: '', stateId: ''});
              this.statesList.set([]); // reset the list for invalid record
              this.countyList.set([]); // reset the list for invalid record
              this.citiesList.set([]); // reset the list for invalid record
              this.filteredStatesList.set([]); // reset the list for invalid record
              this.filteredCountyList.set([]); // reset the list for invalid record
              this.filteredCityList.set([]); // reset the list for invalid record
            }
          },
          error: (error: any) => {
            this.branchLlyodForm.patchValue({city: '', country: '', state: ''});
            this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
          }
        })
      }

      fetchCountyAndCity(stateId: number, stateList: any[], countiesList: any[] = [], citiesList: any[] = []){
          this.isLoading.set(true);
          forkJoin([
            this.propertyService.getStates(),
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId),
          ]).pipe(
            map(([statesList, countyList, cityList]) => { return {statesList, countyList, cityList}})
          ).subscribe({
            next: (response: any) => {
                if(response){
                  this.isLoading.set(false);
                  this.updateStateList( response.statesList, stateList);
                  
                  const countiesSortedList = this.propertyService.sortCountyRecords(response.countyList);
                  const citiesSortedList = this.propertyService.sortCityRecords(response.cityList);
                  if(countiesList?.length > 0 && citiesList?.length > 0){
                    const filteredCounties = countiesSortedList.filter(countyId => countiesList.includes(countyId.countyId));
                    const filteredCities = citiesSortedList.filter(cityId => citiesList.includes(cityId.cityId));
                    this.updateCountyAndCityList(filteredCounties, filteredCities);
                  }else{
                    this.updateCountyAndCityList(countiesSortedList, citiesSortedList);
                  }
                }
            },
            error: (error: any) => {
                this.isLoading.set(false);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
          })
        }

     updateStateList(stateList: any[], stateIDList: any[]) {
            const filteredStates = stateList.filter(state => stateIDList.includes(state.stateId));
            this.statesList.set(filteredStates);
            this.filteredStatesList.set(filteredStates);
        }

        updateCountyAndCityList(countiesList: any[] = [], citiesList: any[] = []) {
            this.countyList.set(countiesList);
            this.citiesList.set(citiesList);
            this.filteredCountyList.set(countiesList);            
            this.filteredCityList.set(citiesList);
            // this.isCityStateListFetched.set(true);
        }

    ngOnDestroy(): void {
        // Unsubscribe to prevent memory leaks
        this.stateSubscription?.unsubscribe();
        this.countySubscription?.unsubscribe();
        this.citySubscription?.unsubscribe();
    }
}   